// src/pages/suppliers/CreateSupplierPage.tsx
import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiX } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import CreateSupplierForm from "@/components/suppliers/forms/CreateSupplierForm";
import {
  type SupplierFormData,
  mapFormToCreateBody,
} from "./useSuppliersPage";

import { useCreateSupplierMutation } from "@/api/features/suppliers/mutations";

/* ---------------------- Breadcrumbs (same as before) ---------------------- */
function Breadcrumbs() {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  const crumbs = parts.map((p, idx) => {
    const href = "/" + parts.slice(0, idx + 1).join("/");
    const label = p
      .split("-")
      .map((s) => s[0]?.toUpperCase() + s.slice(1))
      .join(" ");
    return { href, label };
  });

  return (
    <nav className="text-xs text-gray-500">
      <ol className="flex flex-wrap items-center gap-1">
        <li>
          <Link to="/" className="hover:text-gray-700">
            Home
          </Link>
        </li>
        {crumbs.map((c, i) => (
          <li key={c.href} className="flex items-center gap-1">
            <span>/</span>
            {i < crumbs.length - 1 ? (
              <Link to={c.href} className="hover:text-gray-700">
                {c.label}
              </Link>
            ) : (
              <span className="text-gray-700">{c.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/* ---------------------- Helper: extract API errors ------------------------ */

type ApiValidationError = {
  ok?: boolean;
  message?: string;
  errors?: {
    formErrors?: string[];
    fieldErrors?: Record<string, string[]>;
  };
};

function extractErrorMessage(err: unknown): string | null {
  if (!err) return null;

  const anyErr = err as any;

  // Our request() wrapper throws Error with { status, payload }
  const data: ApiValidationError | undefined =
    anyErr?.response?.data ?? // axios-style
    anyErr?.data ?? // generic
    anyErr?.payload ?? // wrapped by request()
    undefined;

  if (!data) {
    const msg = anyErr?.message ?? String(err);
    return msg || null;
  }

  const messages: string[] = [];

  if (data.message) {
    messages.push(data.message);
  }

  if (data.errors?.formErrors && data.errors.formErrors.length > 0) {
    messages.push(...data.errors.formErrors);
  }

  if (data.errors?.fieldErrors) {
    for (const [field, errs] of Object.entries(data.errors.fieldErrors)) {
      if (!errs || errs.length === 0) continue;
      errs.forEach((m) => {
        messages.push(`${field}: ${m}`);
      });
    }
  }

  if (messages.length === 0) {
    return null;
  }

  // Our form splits on "|" and shows them as bullets
  return messages.join(" | ");
}

/* ============================================================================
   Page: Create Supplier
============================================================================ */
export default function CreateSupplierPage() {
  const navigate = useNavigate();

  const createMutation = useCreateSupplierMutation();

  const handleSubmit = (payload: SupplierFormData) => {
    // Map UI form → deep API body (base + children),
    // mapFormToCreateBody already aligned with our new schema.
    const body = mapFormToCreateBody(payload);

    createMutation.mutate(body, {
      onSuccess: () => {
        navigate("/suppliers");
      },
    });
  };

  const handleCancel = () => {
    navigate("/suppliers");
  };

  const errorMessage = extractErrorMessage(createMutation.error);

  // Optional: log errors for debugging
  useEffect(() => {
    if (createMutation.error) {
      // eslint-disable-next-line no-console
      console.error(
        "[CreateSupplier] mutation error:",
        createMutation.error
      );
    }
  }, [createMutation.error]);

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Add Supplier</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>

        <button
          type="button"
          onClick={handleCancel}
          className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <FiX />
          Back to Suppliers
        </button>
      </div>

      {/* Form card */}
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
        <CreateSupplierForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          submitting={createMutation.isPending}
          errorMessage={errorMessage}
        />
      </section>
    </AdminLayout>
  );
}
